import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Plus, X, Pencil } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const SUPPORT_LEVELS = [
  { value: 'independent', label: 'Independent' },
  { value: 'semi', label: 'Semi-Independent' },
  { value: 'full', label: 'Full Support' },
];

const COMM_TYPES = [
  { value: 'verbal', label: 'Verbal' },
  { value: 'nonverbal', label: 'Nonverbal' },
  { value: 'limited', label: 'Limited' },
  { value: 'assisted', label: 'Assisted' },
];

const SEVERITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];

const ASSIST_LEVELS = [
  { value: 'prompting', label: 'Prompting' },
  { value: 'partial', label: 'Partial' },
  { value: 'full', label: 'Full' },
];

const PHOTO_STATUSES = [
  { value: 'missing', label: 'Missing' },
  { value: 'compliant', label: 'Compliant' },
  { value: 'due_soon', label: 'Due Soon' },
  { value: 'overdue', label: 'Overdue' },
];

export function SupportSnapshotEditDrawer({ participantId, snapshot, open, onClose, onSaved }) {
  const [form, setForm] = useState({
    primary_disability: '',
    support_level: 'independent',
    mobility: { wheelchair: false, walking_aid: false, transfer_required: false, notes: '' },
    communication: { type: 'verbal', notes: '' },
    alerts: [],
    assistance_required: [],
    photo_compliance: { status: 'missing', review_due: '' },
    quick_notes: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (snapshot && open) {
      setForm({
        primary_disability: snapshot.primary_disability || '',
        support_level: snapshot.support_level || 'independent',
        mobility: snapshot.mobility || { wheelchair: false, walking_aid: false, transfer_required: false, notes: '' },
        communication: snapshot.communication || { type: 'verbal', notes: '' },
        alerts: (snapshot.alerts || []).map(a => ({ ...a })),
        assistance_required: (snapshot.assistance_required || []).map(a => ({ ...a })),
        photo_compliance: {
          status: snapshot.photo_compliance?.status || 'missing',
          review_due: snapshot.photo_compliance?.review_due ? snapshot.photo_compliance.review_due.split('T')[0] : '',
        },
        quick_notes: snapshot.quick_notes || '',
      });
    }
  }, [snapshot, open]);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));
  const setMobility = (key, val) => setForm(f => ({ ...f, mobility: { ...f.mobility, [key]: val } }));
  const setComm = (key, val) => setForm(f => ({ ...f, communication: { ...f.communication, [key]: val } }));
  const setPhoto = (key, val) => setForm(f => ({ ...f, photo_compliance: { ...f.photo_compliance, [key]: val } }));

  const addAlert = () => setForm(f => ({ ...f, alerts: [...f.alerts, { type: '', severity: 'medium', active: true, notes: '', review_due_at: '' }] }));
  const removeAlert = (i) => setForm(f => ({ ...f, alerts: f.alerts.filter((_, idx) => idx !== i) }));
  const updateAlert = (i, key, val) => setForm(f => ({ ...f, alerts: f.alerts.map((a, idx) => idx === i ? { ...a, [key]: val } : a) }));

  const addAssist = () => setForm(f => ({ ...f, assistance_required: [...f.assistance_required, { category: '', level: 'partial', notes: '' }] }));
  const removeAssist = (i) => setForm(f => ({ ...f, assistance_required: f.assistance_required.filter((_, idx) => idx !== i) }));
  const updateAssist = (i, key, val) => setForm(f => ({ ...f, assistance_required: f.assistance_required.map((a, idx) => idx === i ? { ...a, [key]: val } : a) }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        alerts: form.alerts.filter(a => a.type.trim()),
        assistance_required: form.assistance_required.filter(a => a.category.trim()),
        photo_compliance: {
          status: form.photo_compliance.status,
          review_due: form.photo_compliance.review_due || null,
        },
      };
      await api.put(`/participants/${participantId}/support-snapshot`, payload);
      toast.success('Support Snapshot updated');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update snapshot');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col" data-testid="snapshot-edit-drawer">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2 text-sm">
            <Pencil className="w-4 h-4" /> Edit Support Snapshot
          </DialogTitle>
          <DialogDescription>Update operational support information for this participant.</DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-auto space-y-5 pr-1">
          {/* A. Support Overview */}
          <section>
            <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">Support Overview</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Primary Disability</Label>
                <Input value={form.primary_disability} onChange={e => set('primary_disability', e.target.value)} className="h-8 text-sm" data-testid="edit-primary-disability" placeholder="e.g., Autism, Cerebral Palsy" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Support Level</Label>
                <Select value={form.support_level} onValueChange={v => set('support_level', v)}>
                  <SelectTrigger className="h-8 text-sm" data-testid="edit-support-level"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SUPPORT_LEVELS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </section>

          {/* B. Mobility */}
          <section>
            <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">Mobility</p>
            <div className="flex gap-4 flex-wrap mb-2">
              {[['wheelchair', 'Wheelchair'], ['walking_aid', 'Walking Aid'], ['transfer_required', 'Transfer Required']].map(([key, label]) => (
                <div key={key} className="flex items-center gap-1.5">
                  <Checkbox id={`mob-${key}`} checked={form.mobility[key]} onCheckedChange={v => setMobility(key, v)} data-testid={`edit-mobility-${key}`} />
                  <Label htmlFor={`mob-${key}`} className="text-xs cursor-pointer">{label}</Label>
                </div>
              ))}
            </div>
            <Input value={form.mobility.notes} onChange={e => setMobility('notes', e.target.value)} placeholder="Mobility notes..." className="h-8 text-sm" data-testid="edit-mobility-notes" />
          </section>

          {/* C. Communication */}
          <section>
            <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">Communication</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Type</Label>
                <Select value={form.communication.type} onValueChange={v => setComm('type', v)}>
                  <SelectTrigger className="h-8 text-sm" data-testid="edit-comm-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {COMM_TYPES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Notes</Label>
                <Input value={form.communication.notes} onChange={e => setComm('notes', e.target.value)} placeholder="e.g., Uses gestures" className="h-8 text-sm" data-testid="edit-comm-notes" />
              </div>
            </div>
          </section>

          {/* D. Alerts */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Alerts</p>
              <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={addAlert} data-testid="add-alert-btn"><Plus className="w-3 h-3 mr-1" /> Add</Button>
            </div>
            {form.alerts.length === 0 && <p className="text-xs text-muted-foreground italic">No alerts configured</p>}
            <div className="space-y-3">
              {form.alerts.map((a, i) => (
                <div key={i} className="space-y-1 p-2 rounded border bg-muted/10" data-testid={`alert-row-${i}`}>
                  <div className="flex gap-2 items-start">
                    <Input value={a.type} onChange={e => updateAlert(i, 'type', e.target.value)} placeholder="Alert type..." className="h-7 text-xs flex-1" />
                    <Select value={a.severity} onValueChange={v => updateAlert(i, 'severity', v)}>
                      <SelectTrigger className="h-7 text-xs w-[90px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {SEVERITY_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <div className="flex items-center gap-1">
                      <Checkbox id={`alert-active-${i}`} checked={a.active !== false} onCheckedChange={v => updateAlert(i, 'active', v)} />
                      <Label htmlFor={`alert-active-${i}`} className="text-[10px]">Active</Label>
                    </div>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" onClick={() => removeAlert(i)}><X className="w-3 h-3" /></Button>
                  </div>
                  <div className="flex gap-2">
                    <Input value={a.notes || ''} onChange={e => updateAlert(i, 'notes', e.target.value)} placeholder="Notes..." className="h-6 text-[10px] flex-1" />
                    <Input type="date" value={a.review_due_at ? a.review_due_at.split('T')[0] : ''} onChange={e => updateAlert(i, 'review_due_at', e.target.value || null)} className="h-6 text-[10px] w-[130px]" title="Review due date" data-testid={`alert-review-due-${i}`} />
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* E. Assistance Required */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Assistance Required</p>
              <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={addAssist} data-testid="add-assist-btn"><Plus className="w-3 h-3 mr-1" /> Add</Button>
            </div>
            {form.assistance_required.length === 0 && <p className="text-xs text-muted-foreground italic">No assistance needs recorded</p>}
            <div className="space-y-2">
              {form.assistance_required.map((a, i) => (
                <div key={i} className="flex gap-2 items-start" data-testid={`assist-row-${i}`}>
                  <Input value={a.category} onChange={e => updateAssist(i, 'category', e.target.value)} placeholder="e.g., Showering" className="h-7 text-xs flex-1" />
                  <Select value={a.level} onValueChange={v => updateAssist(i, 'level', v)}>
                    <SelectTrigger className="h-7 text-xs w-[100px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ASSIST_LEVELS.map(l => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" onClick={() => removeAssist(i)}><X className="w-3 h-3" /></Button>
                </div>
              ))}
            </div>
          </section>

          {/* F. Photo Compliance */}
          <section>
            <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">Photo Compliance</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Status</Label>
                <Select value={form.photo_compliance.status} onValueChange={v => setPhoto('status', v)}>
                  <SelectTrigger className="h-8 text-sm" data-testid="edit-photo-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PHOTO_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Review Due</Label>
                <Input type="date" value={form.photo_compliance.review_due} onChange={e => setPhoto('review_due', e.target.value)} className="h-8 text-sm" data-testid="edit-photo-review-due" />
              </div>
            </div>
          </section>

          {/* G. Quick Notes */}
          <section>
            <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">Quick Notes</p>
            <textarea
              value={form.quick_notes}
              onChange={e => set('quick_notes', e.target.value)}
              placeholder="Any quick notes for support staff..."
              className="w-full min-h-[60px] rounded-lg border bg-background px-3 py-2 text-sm resize-y"
              data-testid="edit-quick-notes"
            />
          </section>
        </div>

        <DialogFooter className="flex-shrink-0 pt-3 border-t">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={handleSave} disabled={saving} data-testid="snapshot-save-btn">
            {saving ? 'Saving...' : 'Save Snapshot'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
